import React, { useEffect, useRef, useState } from 'react'
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Alert,
  Switch,
  Platform,
  Image,
  FlatList } from 'react-native'
import AsyncStorage from '@react-native-async-storage/async-storage'
import { X, Store, Handshake, Check, ImagePlus, Plus, CheckCircle, AlertCircle, Loader } from 'lucide-react-native'
import { CameraView, useCameraPermissions } from 'expo-camera'
import * as ImagePicker from 'expo-image-picker'
import * as FileSystem from 'expo-file-system/legacy'
import { supabase } from '../lib/supabase'
import { getProductSuggestions } from '../lib/openrouter'
import { useAuth } from '../contexts/AuthContext'
import { useLanguage } from '@fliisi/i18n'
import { useToast } from '../components/Toast'
import { NativeStackNavigationProp } from '@react-navigation/native-stack'
import { RouteProp } from '@react-navigation/native'
import { RootStackParamList } from '../types/navigation'
import theme from '../theme'
import {
  Button,
  ButtonText,
  Card,
  Spinner,
  XStack,
  YStack } from '@fliisi/pollen'

// Store/Merchant variant colors (Terracotta theme)
const merchantColors = {
  primary: '#C07A5F',      // Deep Terracotta
  primaryLight: '#D4A088', // Light Terracotta
  accent: '#748C70',       // Sage Green
}

interface Props {
  navigation: NativeStackNavigationProp<RootStackParamList, 'StoreCamera'>
  route: RouteProp<RootStackParamList, 'StoreCamera'>
}

interface UploadStatus {
  productId: string
  status: 'uploading' | 'success' | 'error'
  previewUri?: string
}

type OwnershipType = 'store_owned' | 'consignment'

export default function StoreCameraScreen({ navigation, route }: Props) {
  const { storeId, pickFromGallery: galleryMode } = route.params
  const { user } = useAuth()
  const toast = useToast()
  const { t } = useLanguage()
  const cameraRef = useRef<CameraView>(null)
  const [permission, requestPermission] = useCameraPermissions()
  const [photoCount, setPhotoCount] = useState(0)
  const [uploadStatuses, setUploadStatuses] = useState<UploadStatus[]>([])
  const [isCapturing, setIsCapturing] = useState(false)
  const [autoLabel, setAutoLabel] = useState(true)
  const [ownershipType, setOwnershipType] = useState<OwnershipType>('store_owned')

  const [galleryItems, setGalleryItems] = useState<UploadStatus[]>([])
  const [galleryDone, setGalleryDone] = useState(false) // all uploads finished?

  // Auto-open gallery if launched in gallery mode — go straight to inventory
  useEffect(() => {
    if (galleryMode) {
      pickForInventory()
    }
  }, [galleryMode])

  // Load preferences
  useEffect(() => {
    AsyncStorage.getItem('autoLabel').then((value) => {
      if (value !== null) {
        setAutoLabel(value === 'true')
      }
    })
    AsyncStorage.getItem('storeOwnershipType').then((value) => {
      if (value === 'consignment' || value === 'store_owned') {
        setOwnershipType(value)
      }
    })
  }, [])

  const toggleAutoLabel = async (value: boolean) => {
    setAutoLabel(value)
    await AsyncStorage.setItem('autoLabel', value.toString())
  }

  const toggleOwnership = async () => {
    const newType: OwnershipType = ownershipType === 'store_owned' ? 'consignment' : 'store_owned'
    setOwnershipType(newType)
    await AsyncStorage.setItem('storeOwnershipType', newType)
  }

  // Process upload in background
  const processUpload = async (
    photoUri: string,
    tempId: string,
    currentOwnership: OwnershipType,
    currentAutoLabel: boolean
  ) => {
    try {
      // Upload image first to get URL
      let fileData: Blob | Uint8Array

      if (Platform.OS === 'web') {
        const response = await fetch(photoUri)
        fileData = await response.blob()
      } else {
        const base64 = await FileSystem.readAsStringAsync(photoUri, {
          encoding: 'base64' })
        fileData = decode(base64)
      }

      const timestamp = tempId.replace('temp-', '')
      const fileName = `${user?.id}/${storeId}/${timestamp}.jpg`

      const { error: uploadError } = await supabase.storage
        .from('product-images')
        .upload(fileName, fileData, { contentType: 'image/jpeg', upsert: true })

      if (uploadError) throw uploadError

      const { data: urlData } = supabase.storage.from('product-images').getPublicUrl(fileName)
      const publicUrl = urlData.publicUrl

      // Stage 1: create "•••" draft immediately → Realtime INSERT fires in inventory
      const { data: productId, error: productError } = await supabase.rpc('add_store_product', {
        p_store_id: storeId,
        p_title: '•••',
        p_status: 'draft',
        p_ownership_type: currentOwnership,
        p_image_url: publicUrl,
      })

      if (productError) throw productError

      // Product visible in inventory — mark success
      setUploadStatuses((prev) =>
        prev.map((s) =>
          s.productId === tempId ? { ...s, productId: productId, status: 'success' } : s
        )
      )

      // Stage 2: AI label in background → Realtime UPDATE fires, titles pop in
      if (currentAutoLabel) {
        try {
          const suggestion = await getProductSuggestions(publicUrl)
          await supabase.from('products').update({
            title: suggestion.title_en || '•••',
            title_fi: suggestion.title_fi || null,
            title_en: suggestion.title_en || null,
            description: suggestion.description_en || null,
            description_fi: suggestion.description_fi || null,
            description_en: suggestion.description_en || null,
            price: suggestion.price || null,
          }).eq('id', productId)
        } catch (aiErr) {
          console.error('AI labeling failed, product stays as draft:', aiErr)
        }
      }
    } catch (error: any) {
      console.error('Error uploading:', error)
      setUploadStatuses((prev) =>
        prev.map((s) =>
          s.productId === tempId ? { ...s, status: 'error' } : s
        )
      )
    }
  }

  const takePicture = async () => {
    if (!cameraRef.current || isCapturing) return

    setIsCapturing(true)
    try {
      const photo = await cameraRef.current.takePictureAsync({
        quality: 0.8,
        skipProcessing: true })

      if (!photo?.uri) throw new Error('Failed to capture photo')

      // Create temp product ID for UI tracking
      const timestamp = Date.now()
      const tempId = `temp-${timestamp}`

      // Immediately update UI and allow next capture
      setPhotoCount((prev) => prev + 1)
      setUploadStatuses((prev) => [
        { productId: tempId, status: 'uploading', previewUri: photo.uri },
        ...prev,
      ])

      // Capture current settings for background upload
      const currentOwnership = ownershipType
      const currentAutoLabel = autoLabel

      // Release the capture lock immediately
      setIsCapturing(false)

      // Process upload in background (non-blocking)
      processUpload(photo.uri, tempId, currentOwnership, currentAutoLabel)
    } catch (error: any) {
      console.error('Error capturing:', error)
      if (Platform.OS === 'web') {
        toast.error('Virhe', error.message)
      } else {
        toast.error('Virhe', error.message)
      }
      setIsCapturing(false)
    }
  }

  const pickFromGalleryImages = async (): Promise<boolean> => {
    try {
      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ["images"],
        allowsMultipleSelection: true,
        quality: 0.8 })

      if (result.canceled || !result.assets || result.assets.length === 0) return false

      // Process each selected image through the same upload pipeline
      for (const asset of result.assets) {
        const timestamp = Date.now() + Math.random().toString(36).slice(2, 6)
        const tempId = `temp-${timestamp}`

        setPhotoCount((prev) => prev + 1)
        setUploadStatuses((prev) => [
          { productId: tempId, status: 'uploading', previewUri: asset.uri },
          ...prev,
        ])

        const currentOwnership = ownershipType
        const currentAutoLabel = autoLabel

        processUpload(asset.uri, tempId, currentOwnership, currentAutoLabel)
      }
      return true
    } catch (error: any) {
      console.error('Error picking from gallery:', error)
      toast.error('Virhe', error.message)
      return false
    }
  }

  // Gallery mode: pick images and show confirmation screen with upload progress
  const pickFromGalleryForConfirmation = async () => {
    try {
      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ["images"],
        allowsMultipleSelection: true,
        quality: 0.8 })

      if (result.canceled || !result.assets || result.assets.length === 0) {
        navigation.goBack()
        return
      }

      const currentOwnership = ownershipType
      const currentAutoLabel = autoLabel

      const newItems: UploadStatus[] = result.assets.map((asset) => {
        const timestamp = Date.now() + Math.random().toString(36).slice(2, 6)
        const tempId = `temp-${timestamp}`
        return { productId: tempId, status: 'uploading' as const, previewUri: asset.uri }
      })

      setGalleryItems((prev) => [...prev, ...newItems])
      setPhotoCount((prev) => prev + newItems.length)

      // Start uploads in background
      for (let i = 0; i < result.assets.length; i++) {
        processGalleryUpload(result.assets[i].uri, newItems[i].productId, currentOwnership, currentAutoLabel)
      }
    } catch (error: any) {
      console.error('Error picking from gallery:', error)
      toast.error('Virhe', error.message)
      navigation.goBack()
    }
  }

  // Separate upload processor that updates galleryItems state
  const processGalleryUpload = async (
    photoUri: string,
    tempId: string,
    currentOwnership: OwnershipType,
    currentAutoLabel: boolean
  ) => {
    try {
      let fileData: Blob | Uint8Array

      if (Platform.OS === 'web') {
        const response = await fetch(photoUri)
        fileData = await response.blob()
      } else {
        const base64 = await FileSystem.readAsStringAsync(photoUri, {
          encoding: 'base64' })
        fileData = decode(base64)
      }

      const timestamp = tempId.replace('temp-', '')
      const fileName = `${user?.id}/${storeId}/${timestamp}.jpg`

      const { error: uploadError } = await supabase.storage
        .from('product-images')
        .upload(fileName, fileData, {
          contentType: 'image/jpeg',
          upsert: true })

      if (uploadError) throw uploadError

      const { data: urlData } = supabase.storage
        .from('product-images')
        .getPublicUrl(fileName)

      const publicUrl = urlData.publicUrl

      // Stage 1: create "•••" draft immediately
      const { data: productId, error: productError } = await supabase.rpc('add_store_product', {
        p_store_id: storeId,
        p_title: '•••',
        p_status: 'draft',
        p_ownership_type: currentOwnership,
        p_image_url: publicUrl,
      })

      if (productError) throw productError

      setGalleryItems((prev) =>
        prev.map((s) =>
          s.productId === tempId ? { ...s, productId: productId, status: 'success' } : s
        )
      )

      // Stage 2: AI label in background
      if (currentAutoLabel) {
        try {
          const suggestion = await getProductSuggestions(publicUrl)
          await supabase.from('products').update({
            title: suggestion.title_en || '•••',
            title_fi: suggestion.title_fi || null,
            title_en: suggestion.title_en || null,
            description: suggestion.description_en || null,
            description_fi: suggestion.description_fi || null,
            description_en: suggestion.description_en || null,
            price: suggestion.price || null,
          }).eq('id', productId)
        } catch (aiErr) {
          console.error('AI labeling failed:', aiErr)
        }
      }
    } catch (error: any) {
      console.error('Error uploading:', error)
      setGalleryItems((prev) =>
        prev.map((s) =>
          s.productId === tempId ? { ...s, status: 'error' } : s
        )
      )
    }
  }

  // Track when all gallery uploads are done
  useEffect(() => {
    if (galleryMode && galleryItems.length > 0) {
      const allDone = galleryItems.every((item) => item.status === 'success' || item.status === 'error')
      setGalleryDone(allDone)
    }
  }, [galleryItems, galleryMode])

  // Two-stage background upload: create product immediately → AI label → update
  // Runs after navigation so the inventory screen receives Realtime events
  const backgroundUploadAndLabel = async (
    photoUri: string,
    userId: string,
    ownership: OwnershipType,
  ) => {
    try {
      let fileData: Blob | Uint8Array
      if (Platform.OS === 'web') {
        const response = await fetch(photoUri)
        fileData = await response.blob()
      } else {
        const base64 = await FileSystem.readAsStringAsync(photoUri, { encoding: 'base64' })
        fileData = decode(base64)
      }

      const timestamp = Date.now() + Math.random().toString(36).slice(2, 6)
      const fileName = `${userId}/${storeId}/${timestamp}.jpg`

      const { error: uploadError } = await supabase.storage
        .from('product-images')
        .upload(fileName, fileData, { contentType: 'image/jpeg', upsert: true })
      if (uploadError) throw uploadError

      const { data: urlData } = supabase.storage
        .from('product-images')
        .getPublicUrl(fileName)
      const publicUrl = urlData.publicUrl

      // Stage 1: create draft immediately → INSERT fires Realtime in inventory
      const { data: productId, error: productError } = await supabase.rpc('add_store_product', {
        p_store_id: storeId,
        p_title: '•••',
        p_image_url: publicUrl,
        p_status: 'draft',
        p_ownership_type: ownership,
      })
      if (productError || !productId) throw productError || new Error('No product ID')

      // Stage 2: AI label → UPDATE fires Realtime, title/desc pop in on inventory card
      try {
        const suggestion = await getProductSuggestions(publicUrl)
        await supabase.from('products').update({
          title: suggestion.title_en || '•••',
          title_fi: suggestion.title_fi || null,
          title_en: suggestion.title_en || null,
          description: suggestion.description_en || null,
          description_fi: suggestion.description_fi || null,
          description_en: suggestion.description_en || null,
          price: suggestion.price || null,
        }).eq('id', productId)
      } catch (aiErr) {
        console.error('AI labeling failed:', aiErr)
        // Product stays as draft with placeholder title — can be edited manually
      }
    } catch (err) {
      console.error('Background upload failed:', err)
    }
  }

  // Pick images from gallery → navigate back to inventory → upload in background
  const pickForInventory = async () => {
    try {
      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ["images"],
        allowsMultipleSelection: true,
        quality: 0.8,
      })

      if (result.canceled || !result.assets || result.assets.length === 0) {
        navigation.goBack()
        return
      }

      const userId = user?.id
      if (!userId) { navigation.goBack(); return }

      const capturedOwnership = ownershipType

      // Go back to inventory immediately
      navigation.goBack()

      // Fire background uploads — promises continue after navigation
      for (const asset of result.assets) {
        backgroundUploadAndLabel(asset.uri, userId, capturedOwnership)
      }
    } catch (err: any) {
      console.error('Gallery picker error:', err)
      navigation.goBack()
    }
  }

  const pickFromGallery = () => pickFromGalleryImages()

  const addMoreFromGallery = () => pickFromGalleryForConfirmation()

  // Helper to decode base64 to Uint8Array
  const decode = (base64: string): Uint8Array => {
    const binaryString = atob(base64)
    const bytes = new Uint8Array(binaryString.length)
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i)
    }
    return bytes
  }

  const finishSession = () => {
    navigation.navigate('StoreInventory', { storeId })
  }

  // Gallery mode: show confirmation screen with upload progress
  if (galleryMode) {
    if (galleryItems.length === 0) {
      // Picker is opening or user hasn't selected yet
      return (
        <View style={styles.galleryContainer}>
          <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
            <Spinner size="large" />
            <Text style={{ color: '#999', fontSize: 15, marginTop: 12 }}>{t('opening_gallery') || 'Opening gallery...'}</Text>
          </View>
        </View>
      )
    }

    const successCount = galleryItems.filter((s) => s.status === 'success').length
    const uploadingCount = galleryItems.filter((s) => s.status === 'uploading').length
    const errorCount = galleryItems.filter((s) => s.status === 'error').length

    return (
      <View style={styles.galleryContainer}>
        {/* Header */}
        <View style={styles.galleryHeader}>
          <TouchableOpacity style={styles.galleryCloseButton} onPress={() => navigation.goBack()}>
            <X size={20} color={merchantColors.primary} strokeWidth={2.5} />
          </TouchableOpacity>
          <View style={styles.galleryHeaderCenter}>
            <Text style={styles.galleryTitle}>
              {galleryDone
                ? (t('upload_complete') || 'Upload complete')
                : (t('uploading_products') || 'Uploading products...')}
            </Text>
            <Text style={styles.gallerySubtitle}>
              {successCount}/{galleryItems.length} {t('ready') || 'ready'}
              {errorCount > 0 ? ` · ${errorCount} ${t('failed') || 'failed'}` : ''}
            </Text>
          </View>
          <View style={{ width: 36 }} />
        </View>

        {/* Progress bar */}
        <View style={styles.progressBarContainer}>
          <View style={[
            styles.progressBar,
            { width: `${galleryItems.length > 0 ? ((successCount + errorCount) / galleryItems.length) * 100 : 0}%` },
            errorCount > 0 && successCount === 0 ? styles.progressBarError : null,
          ]} />
        </View>

        {/* Image grid */}
        <FlatList
          data={galleryItems}
          numColumns={3}
          keyExtractor={(item) => item.productId}
          contentContainerStyle={styles.galleryGrid}
          renderItem={({ item }) => (
            <View style={styles.galleryItemWrapper}>
              <Image
                source={{ uri: item.previewUri }}
                style={styles.galleryItemImage}
              />
              <View style={[
                styles.galleryItemOverlay,
                item.status === 'success' && styles.galleryItemSuccess,
                item.status === 'error' && styles.galleryItemError,
              ]}>
                {item.status === 'uploading' && (
                  <Spinner size="small" />
                )}
                {item.status === 'success' && (
                  <CheckCircle size={24} color="#fff" />
                )}
                {item.status === 'error' && (
                  <AlertCircle size={24} color="#fff" />
                )}
              </View>
            </View>
          )}
        />

        {/* Bottom actions */}
        <View style={styles.galleryBottomBar}>
          <TouchableOpacity style={styles.addMoreButton} onPress={addMoreFromGallery}>
            <Plus size={20} color={merchantColors.primary} />
            <Text style={styles.addMoreText}>{t('add_more') || 'Add more'}</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.doneButton, !galleryDone && styles.doneButtonDisabled]}
            onPress={() => {
              if (galleryDone) {
                toast.success(
                  t('products_added') || 'Products added',
                  `${successCount} ${successCount === 1 ? (t('product') || 'product') : (t('products') || 'products')} ${t('added_to_store') || 'added to store'}`
                )
              }
              navigation.goBack()
            }}
          >
            {!galleryDone && <Spinner size="small" color="#fff" style={{ marginRight: 8 }} />}
            <Text style={styles.doneButtonText}>
              {galleryDone
                ? (t('done') || 'Done')
                : `${t('uploading') || 'Uploading'}... (${successCount}/${galleryItems.length})`}
            </Text>
          </TouchableOpacity>
        </View>
      </View>
    )
  }

  if (!permission) {
    return (
      <View style={styles.container}>
        <Spinner size="large" />
      </View>
    )
  }

  if (!permission.granted) {
    return (
      <View style={styles.container}>
        <Text style={styles.permissionText}>
          {t('camera_permission') || 'Camera permission is required to add products'}
        </Text>
        <TouchableOpacity style={styles.permissionButton} onPress={requestPermission}>
          <Text style={styles.permissionButtonText}>
            {t('grant_permission') || 'Grant Permission'}
          </Text>
        </TouchableOpacity>
      </View>
    )
  }

  const uploadingCount = uploadStatuses.filter((s) => s.status === 'uploading').length
  const successCount = uploadStatuses.filter((s) => s.status === 'success').length
  const errorCount = uploadStatuses.filter((s) => s.status === 'error').length

  return (
    <View style={styles.container}>
      <CameraView
        ref={cameraRef}
        style={styles.camera}
        facing="back"
      >
        {/* Top status bar */}
        <View style={styles.statusBar}>
          <TouchableOpacity style={styles.closeButton} onPress={finishSession}>
            <X size={20} color="#fff" strokeWidth={2.5} />
          </TouchableOpacity>

          <View style={styles.togglesContainer}>
            {/* AI Label Toggle */}
            <View style={styles.toggle}>
              <Text style={styles.toggleText}>{t('ai_label') || 'AI Label'}</Text>
              <Switch
                value={autoLabel}
                onValueChange={toggleAutoLabel}
                trackColor={{ false: '#767577', true: merchantColors.primary }}
                thumbColor={autoLabel ? merchantColors.primaryLight : '#f4f3f4'}
              />
            </View>

            {/* Ownership Type Toggle */}
            <TouchableOpacity style={styles.ownershipToggle} onPress={toggleOwnership}>
              <View style={[
                styles.ownershipIndicator,
                ownershipType === 'store_owned' ? styles.storeOwned : styles.consignment
              ]}>
                {ownershipType === 'store_owned' ? (
                  <Store size={14} color="#fff" />
                ) : (
                  <Handshake size={14} color="#fff" />
                )}
              </View>
              <Text style={styles.ownershipText}>
                {ownershipType === 'store_owned'
                  ? (t('store_owned') || 'Store')
                  : (t('consignment') || 'Consign')}
              </Text>
            </TouchableOpacity>
          </View>

          <View style={styles.counters}>
            <View style={styles.counter}>
              <Text style={styles.counterValue}>{photoCount}</Text>
              <Text style={styles.counterLabel}>{t('captured') || 'Captured'}</Text>
            </View>
            {uploadingCount > 0 && (
              <View style={[styles.counter, styles.uploadingCounter]}>
                <Spinner size="small" />
                <Text style={styles.counterLabel}>{uploadingCount}</Text>
              </View>
            )}
            {successCount > 0 && (
              <View style={[styles.counter, styles.successCounter]}>
                <Check size={14} color="#fff" />
                <Text style={styles.counterValue}>{successCount}</Text>
              </View>
            )}
            {errorCount > 0 && (
              <View style={[styles.counter, styles.errorCounter]}>
                <X size={14} color="#fff" />
                <Text style={styles.counterValue}>{errorCount}</Text>
              </View>
            )}
          </View>
        </View>

        {/* Bottom bar: Gallery + Capture */}
        <View style={styles.bottomBar}>
          <TouchableOpacity style={styles.galleryButton} onPress={pickFromGallery}>
            <ImagePlus size={26} color="#fff" />
            <Text style={styles.galleryButtonText}>{t('gallery') || 'Gallery'}</Text>
          </TouchableOpacity>

          <View style={styles.captureButtonOuter}>
            <TouchableOpacity
              style={[
                styles.captureButton,
                isCapturing && styles.captureButtonDisabled,
              ]}
              onPress={takePicture}
              disabled={isCapturing}
            >
              {isCapturing && (
                <Spinner color={merchantColors.primary} style={styles.captureSpinner} />
              )}
            </TouchableOpacity>
          </View>

          {/* Empty spacer to center the capture button */}
          <View style={styles.galleryButtonPlaceholder} />
        </View>
      </CameraView>
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#000',
    justifyContent: 'center',
    alignItems: 'center' },
  camera: {
    flex: 1,
    width: '100%' },
  statusBar: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    paddingTop: 16,
    paddingHorizontal: 16,
    flexWrap: 'wrap' },
  closeButton: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center' },
  togglesContainer: {
    flexDirection: 'column',
    gap: 8 },
  toggle: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(0,0,0,0.5)',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 20,
    gap: 8 },
  toggleText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '600' },
  ownershipToggle: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(0,0,0,0.5)',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 20,
    gap: 8 },
  ownershipIndicator: {
    width: 28,
    height: 28,
    borderRadius: 14,
    justifyContent: 'center',
    alignItems: 'center' },
  storeOwned: {
    backgroundColor: merchantColors.primary },
  consignment: {
    backgroundColor: theme.colors.brand.secondary },
  ownershipText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '600' },
  counters: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 8 },
  counter: {
    backgroundColor: 'rgba(0,0,0,0.5)',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 16,
    alignItems: 'center',
    flexDirection: 'row',
    gap: 6 },
  counterValue: {
    color: '#fff',
    fontSize: 16,
    fontWeight: 'bold' },
  counterLabel: {
    color: '#fff',
    fontSize: 12,
    opacity: 0.8 },
  uploadingCounter: {
    backgroundColor: 'rgba(255,152,0,0.8)' },
  successCounter: {
    backgroundColor: 'rgba(76,175,80,0.8)' },
  errorCounter: {
    backgroundColor: 'rgba(244,67,54,0.8)' },
  bottomBar: {
    position: 'absolute',
    bottom: 40,
    left: 0,
    right: 0,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 32 },
  galleryButton: {
    width: 64,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4 },
  galleryButtonText: {
    color: '#fff',
    fontSize: 11,
    fontWeight: '600',
    textShadowColor: 'rgba(0,0,0,0.5)',
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 2 },
  galleryButtonPlaceholder: {
    width: 64 },
  captureButtonOuter: {
    width: 80,
    height: 80,
    borderRadius: 40,
    borderWidth: 4,
    borderColor: merchantColors.primary,
    justifyContent: 'center',
    alignItems: 'center' },
  captureButton: {
    width: 64,
    height: 64,
    borderRadius: 32,
    backgroundColor: '#fff',
    justifyContent: 'center',
    alignItems: 'center' },
  captureButtonDisabled: {
    backgroundColor: '#ccc' },
  captureSpinner: {
    position: 'absolute' },
  permissionText: {
    color: '#fff',
    fontSize: 16,
    textAlign: 'center',
    marginBottom: 16,
    paddingHorizontal: 32 },
  permissionButton: {
    backgroundColor: merchantColors.primary,
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: theme.borderRadius.sm },
  permissionButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600' },
  // Gallery confirmation screen
  galleryContainer: {
    flex: 1,
    backgroundColor: '#fff' },
  galleryHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingTop: 16,
    paddingHorizontal: 16,
    paddingBottom: 12,
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0' },
  galleryCloseButton: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: `${merchantColors.primary}15`,
    justifyContent: 'center',
    alignItems: 'center' },
  galleryHeaderCenter: {
    alignItems: 'center',
    flex: 1 },
  galleryTitle: {
    fontSize: 17,
    fontWeight: '700',
    color: '#1a1a1a' },
  gallerySubtitle: {
    fontSize: 13,
    color: '#888',
    marginTop: 2 },
  progressBarContainer: {
    height: 3,
    backgroundColor: '#f0f0f0' },
  progressBar: {
    height: 3,
    backgroundColor: merchantColors.accent,
    borderRadius: 1.5 },
  progressBarError: {
    backgroundColor: '#E53E3E' },
  galleryGrid: {
    padding: 8 },
  galleryItemWrapper: {
    flex: 1 / 3,
    aspectRatio: 1,
    padding: 4 },
  galleryItemImage: {
    width: '100%',
    height: '100%',
    borderRadius: 12 },
  galleryItemOverlay: {
    position: 'absolute',
    top: 4,
    left: 4,
    right: 4,
    bottom: 4,
    borderRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0,0,0,0.3)' },
  galleryItemSuccess: {
    backgroundColor: 'rgba(72,187,120,0.5)' },
  galleryItemError: {
    backgroundColor: 'rgba(229,62,62,0.5)' },
  galleryBottomBar: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 14,
    paddingBottom: 28,
    backgroundColor: '#fff',
    borderTopWidth: 1,
    borderTopColor: '#f0f0f0',
    gap: 12 },
  addMoreButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 12,
    borderWidth: 1.5,
    borderColor: merchantColors.primary },
  addMoreText: {
    fontSize: 15,
    fontWeight: '600',
    color: merchantColors.primary },
  doneButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 14,
    borderRadius: 12,
    backgroundColor: merchantColors.primary },
  doneButtonDisabled: {
    backgroundColor: merchantColors.primaryLight },
  doneButtonText: {
    fontSize: 16,
    fontWeight: '700',
    color: '#fff' } })
